import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaSync, FaSave, FaTimes, FaQuestionCircle } from "react-icons/fa";
import PageHeader from "../../core/common/header/PageHeader";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const LEVELS = ["A1", "A2", "B1", "B2", "AP", "B1_Exam_Preparation"];
const CHAPTERS = Array.from({ length: 14 }, (_, i) => `Chapter ${i + 1}`);

const EditQuizPage: React.FC = () => {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState({ title: "", chapter: "", level: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>("");

  const fetchQuiz = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/quizzes/mentor`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch quizzes");
      const data = await res.json();
      const found = data.find(
        (q: { id: any }) => String(q.id) === String(quizId)
      );
      if (found) {
        setQuiz(found);
      } else {
        setError("Quiz not found");
      }
    } catch (err: any) {
      setError(err.message || "Failed to load quiz");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuiz();
  }, [quizId]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setQuiz((q) => ({ ...q, [name]: value }));

    // Auto-update title when chapter or level changes
    if (name === "chapter" || name === "level") {
      const chapter = name === "chapter" ? value : quiz.chapter;
      const level = name === "level" ? value : quiz.level;

      if (chapter && level) {
        setQuiz((q) => ({
          ...q,
          title: `${chapter} - ${level.replace("_", " ")}`,
          [name]: value,
        }));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/quizzes/mentor/${quizId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          title: quiz.title,
          chapter: quiz.chapter,
          level: quiz.level,
        }),
      });
      if (!res.ok) throw new Error("Failed to update quiz");
      navigate("/admin/manage-quizzes");
    } catch (err: any) {
      setError(err.message || "Failed to update quiz");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/admin/manage-quizzes");
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaQuestionCircle />}
        title="Edit Quiz"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Manage Quizzes", to: "/admin/manage-quizzes" },
          { label: "Edit Quiz" },
        ]}
      />

      <div className="flex justify-end gap-2 max-w-7xl mx-auto px-4 sm:px-6 pt-4">
        <button
          onClick={fetchQuiz}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors disabled:opacity-50 text-sm sm:text-base backdrop-blur-sm"
          style={{ fontSize: "16px" }}
        >
          <FaSync className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {loading ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <FaSync className="animate-spin h-12 w-12 text-[#009fe3] mx-auto mb-4" />
            <p className="text-gray-600">Loading quiz details...</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded">
              <p className="font-semibold">Error</p>
              <p>{error}</p>
            </div>
            <button
              onClick={handleCancel}
              className="w-full bg-gray-500 hover:bg-gray-600 text-white py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
            >
              <FaTimes />
              Back to Quizzes
            </button>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-xl shadow-lg p-6 sm:p-8"
          >
            <div className="space-y-6 mb-8">
              {/* Level Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Level <span className="text-red-500">*</span>
                </label>
                <select
                  name="level"
                  value={quiz.level}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                  style={{ fontSize: "16px" }}
                >
                  <option value="">Select Level</option>
                  {LEVELS.map((lvl) => (
                    <option key={lvl} value={lvl}>
                      {lvl.replace("_", " ")}
                    </option>
                  ))}
                </select>
              </div>

              {/* Chapter Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Chapter <span className="text-red-500">*</span>
                </label>
                <select
                  name="chapter"
                  value={quiz.chapter}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                  style={{ fontSize: "16px" }}
                >
                  <option value="">Select Chapter</option>
                  {CHAPTERS.map((chapter) => (
                    <option key={chapter} value={chapter}>
                      {chapter}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  All levels have 14 chapters
                </p>
              </div>

              {/* Quiz Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quiz Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={quiz.title}
                  onChange={handleChange}
                  placeholder="e.g. Chapter 1 - A1"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                  style={{ fontSize: "16px" }}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Auto-generated from chapter and level, but you can edit it
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 bg-[#009fe3] hover:bg-[#007cb5] text-white py-3 rounded-lg font-semibold shadow-md hover:shadow-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <FaSave />
                    Save Changes
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold shadow-md transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <FaTimes />
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Preview Card - Only show when not loading and no error */}
        {!loading && !error && quiz.level && quiz.chapter && (
          <div className="mt-6 bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6 shadow-lg">
            <h3 className="text-sm font-semibold text-gray-600 mb-3">
              📋 Current Quiz Info
            </h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Quiz ID:</span>
                <span className="font-semibold text-[#009fe3]">{quizId}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Level:</span>
                <span className="font-semibold text-[#009fe3]">
                  {quiz.level.replace("_", " ")}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Chapter:</span>
                <span className="font-semibold text-[#009fe3]">
                  {quiz.chapter}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Title:</span>
                <span className="font-semibold text-[#009fe3]">
                  {quiz.title}
                </span>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default EditQuizPage;
